import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Flame,
  Sparkles,
  Award,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Trophy,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { celebrateRecord } from "@/lib/confetti";
import { speak } from "@/lib/tts";
import ShareButton from "@/components/share/ShareButton";

const LOCALE_MAP = { es: "es-ES", en: "en-US", pt: "pt-BR", hi: "hi-IN" };

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function formatShortLocal(dateStr, lng) {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    // Use UTC to avoid local-TZ surprises; we only want "d mes" labels.
    const dt = new Date(Date.UTC(y, m - 1, d));
    return new Intl.DateTimeFormat(LOCALE_MAP[lng] || "es-ES", {
      day: "numeric", month: "short", timeZone: "UTC",
    }).format(dt).replace(".", "");
  } catch {
    return dateStr;
  }
}

function StreakHero({ streak, streakBest }) {
  const { t } = useTranslation();
  const isBroken = streak === 0;
  const isOnFire = streak >= 7;
  const showBest = typeof streakBest === "number" && streakBest > 0;

  return (
    <section
      className="nx-glass rounded-[24px] px-6 py-6 flex items-center gap-5 animate-fade-in-up"
      data-testid="metricas-streak-card"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#00E5FF]/75">
          {t("metricas.streak.label")}
        </p>
        {isBroken ? (
          <p
            className="mt-2 text-[17px] leading-snug font-semibold text-[#E5E7EB]"
            data-testid="metricas-streak-empty"
          >
            {t("metricas.streak.zero_motivation")}
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-2 mt-1">
              <span
                className="font-mono font-bold tabular-nums text-[64px] sm:text-[72px] leading-none text-[#00E5FF] [text-shadow:0_0_24px_rgba(0,229,255,0.5)]"
                data-testid="metricas-streak"
              >
                {streak}
              </span>
            </div>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              {t("metricas.streak.days", { n: streak })}
            </p>
          </>
        )}
        {showBest ? (
          <p
            className="mt-2 flex items-center gap-1.5 text-[11.5px] text-[#9CA3AF]"
            data-testid="metricas-streak-best"
          >
            <Trophy className="h-3 w-3 text-[#FCD34D]/80" strokeWidth={1.75} />
            {t("metricas.streak.record_label", { n: streakBest })}
          </p>
        ) : null}
      </div>

      <div
        className={`
          relative h-[90px] w-[90px] rounded-full flex items-center justify-center
          ${isBroken
            ? "bg-white/[0.02] border border-white/[0.06]"
            : "bg-[rgba(0,229,255,0.06)] border border-[rgba(0,229,255,0.3)] shadow-[0_0_36px_rgba(0,229,255,0.25)]"}
          ${isOnFire ? "animate-pulse-cyan" : ""}
        `}
        aria-hidden="true"
      >
        <Flame
          className={`
            h-[48px] w-[48px]
            ${isBroken
              ? "text-[#4B5563]"
              : "text-[#00E5FF] [filter:drop-shadow(0_0_10px_rgba(0,229,255,0.6))]"}
          `}
          strokeWidth={1.75}
        />
      </div>
    </section>
  );
}

function RecordBanner({ streak, onDismiss }) {
  const { t } = useTranslation();
  return (
    <section
      className="
        relative rounded-[20px] px-5 py-4
        bg-[rgba(0,229,255,0.08)] backdrop-blur-xl
        border border-[rgba(0,229,255,0.5)]
        shadow-[0_0_40px_-5px_rgba(0,229,255,0.55)]
        flex items-center gap-4 animate-fade-in-up
      "
      data-testid="metricas-record-banner"
      role="status"
    >
      <div
        className="
          h-12 w-12 rounded-full bg-[rgba(0,229,255,0.18)]
          border border-[rgba(0,229,255,0.5)]
          shadow-[0_0_18px_rgba(0,229,255,0.5)]
          flex items-center justify-center shrink-0
        "
        aria-hidden="true"
      >
        <Trophy
          className="h-6 w-6 text-[#FCD34D] [filter:drop-shadow(0_0_6px_rgba(252,211,77,0.6))]"
          strokeWidth={1.75}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]/85">
          {t("metricas.record.banner_title")}
        </p>
        <p className="mt-0.5 text-[14.5px] font-semibold text-[#E5E7EB]">
          {t("metricas.record.banner_subtitle", { n: streak })}
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="metricas-record-dismiss"
        aria-label={t("common.close")}
        className="h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.04] hover:text-[#E5E7EB]"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>
    </section>
  );
}

function ChartTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload[0]) return null;
  const p = payload[0].payload;
  // Phase 10: total is now variable per-day. Fall back to `completed` when a
  // legacy point is missing the field so the tooltip still renders.
  const total = typeof p.total === "number" ? p.total : p.completed;
  return (
    <div
      className="
        rounded-xl px-3 py-2
        bg-[rgba(10,14,26,0.92)] backdrop-blur-xl
        border border-[rgba(0,229,255,0.3)]
        shadow-[0_10px_30px_-5px_rgba(0,0,0,0.6),0_0_18px_-4px_rgba(0,229,255,0.35)]
      "
      data-testid="metricas-chart-tooltip"
    >
      <p className="font-mono text-[14px] font-semibold text-[#00E5FF] tabular-nums">
        {t("logros.counter", { done: p.completed, total })}
      </p>
      <p className="text-[11px] text-[#9CA3AF] mt-0.5">{p.displayDate}</p>
    </div>
  );
}

function SevenDaysChart({ last_7_days }) {
  const { t, i18n } = useTranslation();
  const data = useMemo(() => {
    const todayStr = last_7_days[last_7_days.length - 1]?.date;
    return last_7_days.map((p) => ({
      ...p,
      total: typeof p.total === "number" ? p.total : 0,
      label: p.date === todayStr ? t("common.today", { defaultValue: p.date === todayStr ? "Hoy" : "" }) || formatShortLocal(p.date, i18n.language) : formatShortLocal(p.date, i18n.language),
      displayDate: formatShortLocal(p.date, i18n.language),
    }));
  }, [last_7_days, i18n.language, t]);

  // Phase 10: Y-axis adapts to the largest N in the window, floored at 9
  // so a lean week doesn't collapse the chart visually. Ticks pick nice
  // divisors of the max (0, max/3, 2*max/3, max).
  const yMax = useMemo(() => {
    const maxTotal = data.reduce((m, d) => Math.max(m, d.total || 0, d.completed || 0), 0);
    return Math.max(maxTotal, 9);
  }, [data]);
  const yTicks = useMemo(() => {
    // 4 ticks: 0, ~1/3, ~2/3, max. Round to integers.
    const step = Math.max(1, Math.round(yMax / 3));
    return [0, step, step * 2, yMax];
  }, [yMax]);

  const allZero = data.every((d) => d.completed === 0);

  return (
    <section
      className="nx-glass rounded-[24px] px-5 sm:px-6 py-5 animate-fade-in-up"
      data-testid="metricas-chart-card"
    >
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h3 className="text-[16px] font-semibold text-[#E5E7EB]">
            {t("metricas.chart.title")}
          </h3>
          <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
            {t("metricas.chart.subtitle")}
          </p>
        </div>
        <p
          className="font-mono text-[10.5px] text-[#9CA3AF] tracking-wide"
          data-testid="metricas-chart-yrange"
        >
          0 – {yMax}
        </p>
      </div>

      {allZero ? (
        <div
          className="h-[220px] flex items-center justify-center text-center px-6"
          data-testid="metricas-chart-empty"
        >
          <p className="text-[13px] text-[#9CA3AF] max-w-[260px] leading-relaxed">
            {t("metricas.chart.empty_state")}
          </p>
        </div>
      ) : (
        <div className="h-[220px] min-h-[200px] -mx-2" data-testid="metricas-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="metricsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fill: "#9CA3AF", fontSize: 11 }}
                axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                tickLine={false}
              />
              <YAxis
                domain={[0, yMax]}
                ticks={yTicks}
                tick={{ fill: "#9CA3AF", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={28}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ stroke: "rgba(0,229,255,0.25)", strokeWidth: 1 }}
              />
              <Area
                type="monotone"
                dataKey="completed"
                stroke="#00E5FF"
                strokeWidth={2}
                fill="url(#metricsGradient)"
                activeDot={{
                  r: 5,
                  fill: "#00E5FF",
                  stroke: "#0A0E1A",
                  strokeWidth: 2,
                  style: { filter: "drop-shadow(0 0 6px rgba(0,229,255,0.8))" },
                }}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function BadgeCard({
  testId,
  Icon,
  title,
  subtitle,
  activeBadgeLabel,
  earned,
  accentClass,
  footer,
  progress,
  rightSlot,
}) {
  return (
    <div
      className={`
        relative rounded-[22px] px-4 py-5 overflow-hidden
        border transition-all duration-300
        ${earned
          ? `${accentClass} bg-white/[0.04]`
          : "border-white/[0.05] bg-white/[0.015]"}
      `}
      data-testid={testId}
      data-earned={earned ? "true" : "false"}
    >
      {earned ? (
        <span
          className="
            absolute top-3 right-3 text-[9px] font-semibold tracking-[0.2em]
            px-2 py-0.5 rounded-full
            bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.5)]
            text-[#00E5FF]
          "
        >
          {activeBadgeLabel}
        </span>
      ) : null}

      <div
        className={`
          h-12 w-12 rounded-full flex items-center justify-center
          ${earned
            ? "bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.45)] shadow-[0_0_22px_rgba(0,229,255,0.35)]"
            : "bg-white/[0.03] border border-white/[0.06]"}
        `}
      >
        <Icon
          className={`h-6 w-6 ${earned ? "text-[#00E5FF] [filter:drop-shadow(0_0_8px_rgba(0,229,255,0.55))]" : "text-[#6B7280]"}`}
          strokeWidth={1.75}
        />
      </div>

      <h4 className={`mt-3 text-[13.5px] font-semibold ${earned ? "text-[#E5E7EB]" : "text-[#9CA3AF]"}`}>
        {title}
      </h4>
      <p className={`mt-1 text-[11.5px] leading-snug ${earned ? "text-[#E5E7EB]/80" : "text-[#6B7280]"}`}>
        {subtitle}
      </p>

      {progress ? (
        <div className="mt-3 flex items-center gap-1.5" data-testid={`${testId}-progress`}>
          {Array.from({ length: progress.total }).map((_, i) => (
            <span
              key={i}
              className={`
                h-1.5 flex-1 rounded-full
                ${i < progress.filled
                  ? "bg-[#00E5FF] shadow-[0_0_6px_rgba(0,229,255,0.6)]"
                  : "bg-white/[0.08]"}
              `}
            />
          ))}
        </div>
      ) : null}

      {footer ? (
        <p className="mt-3 text-[10.5px] tracking-wide text-[#9CA3AF]" data-testid={`${testId}-total`}>
          {footer}
        </p>
      ) : null}

      {rightSlot ? (
        <div className="mt-3 flex justify-end" data-testid={`${testId}-actions`}>
          {rightSlot}
        </div>
      ) : null}
    </div>
  );
}

function BadgesGrid({ estrella, sello, completadosHoy, totalHoy }) {
  const { t } = useTranslation();
  const estrellaSubtitle = estrella.ganada_hoy
    ? (totalHoy > 0
        ? t("metricas.estrella.subtitle_active_dynamic", { n: totalHoy })
        : t("metricas.estrella.subtitle_active"))
    : t("metricas.estrella.subtitle_inactive");
  const selloSubtitle = sello.ganado_esta_semana
    ? t("metricas.sello.subtitle_active")
    : t("metricas.sello.weekly_progress", { done: sello.dias_perfectos_esta_semana });

  return (
    <section className="grid grid-cols-2 gap-3 animate-fade-in-up" data-testid="metricas-badges-grid">
      <BadgeCard
        testId="metricas-badge-estrella"
        Icon={Sparkles}
        title={t("metricas.estrella.title")}
        subtitle={estrellaSubtitle}
        activeBadgeLabel={t("metricas.estrella.today_badge")}
        earned={estrella.ganada_hoy}
        accentClass="border-[rgba(0,229,255,0.45)] shadow-[0_0_30px_-6px_rgba(0,229,255,0.45)]"
        footer={t("metricas.estrella.total", { n: estrella.total })}
        rightSlot={
          estrella.ganada_hoy ? (
            <ShareButton
              variant="secondary"
              showLabel
              testId="metricas-estrella-share"
              className="!h-8 !text-[11.5px] !px-3"
            />
          ) : null
        }
      />
      <BadgeCard
        testId="metricas-badge-sello"
        Icon={Award}
        title={t("metricas.sello.title")}
        subtitle={selloSubtitle}
        activeBadgeLabel={t("metricas.sello.this_week_badge")}
        earned={sello.ganado_esta_semana}
        accentClass="border-[rgba(99,102,241,0.55)] shadow-[0_0_30px_-6px_rgba(99,102,241,0.5)]"
        footer={t("metricas.sello.total", { n: sello.total })}
        progress={sello.ganado_esta_semana ? null : { filled: sello.dias_perfectos_esta_semana, total: 5 }}
      />
    </section>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-fade-in-up" data-testid="metricas-loading">
      <div className="h-[170px] nx-glass rounded-[24px] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#00E5FF]/70 animate-spin" strokeWidth={1.75} />
      </div>
      <div className="h-[240px] nx-glass rounded-[24px]" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-[150px] nx-glass rounded-[22px]" />
        <div className="h-[150px] nx-glass rounded-[22px]" />
      </div>
    </div>
  );
}

export default function MetricasPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecordBanner, setShowRecordBanner] = useState(false);
  const celebratedKeyRef = useRef(null);
  const bannerTimerRef = useRef(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tz = getTimezone();
      const res = await api.get(`/metricas?tz=${encodeURIComponent(tz)}`);
      setData(res.data);
    } catch (e) {
      setError(t("metricas.load_error", { defaultValue: "No se pudieron cargar las métricas. Reintenta." }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Celebrate a brand-new personal best: confetti + TTS + dismissable banner.
  // The backend flag `is_new_record_today` is true exactly once per day, so
  // the logic here is intentionally simple — we just add a ref-based guard so
  // multiple rapid re-renders within the same mount can't double-fire.
  useEffect(() => {
    if (!data?.is_new_record_today) return;
    const key = `${data.today}:${data.streak}`;
    if (celebratedKeyRef.current === key) return;
    celebratedKeyRef.current = key;

    celebrateRecord();
    speak(
      t("metricas.record.banner_subtitle", { n: data.streak }),
      i18n.language || "es",
    );
    setShowRecordBanner(true);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setShowRecordBanner(false), 6000);
  }, [data, t, i18n.language]);

  useEffect(
    () => () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    },
    [],
  );

  if (loading) {
    return (
      <div className="pt-2" data-testid="metricas-page">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="h-full min-h-[60vh] flex flex-col items-center justify-center text-center px-6 gap-4"
        data-testid="metricas-page"
      >
        <div className="h-14 w-14 rounded-full nx-glass flex items-center justify-center">
          <AlertTriangle className="h-7 w-7 text-[#F59E0B]" strokeWidth={1.75} />
        </div>
        <p className="text-[13px] text-[#E5E7EB] max-w-[260px]" data-testid="metricas-error">
          {error || t("metricas.no_data", { defaultValue: "Sin datos." })}
        </p>
        <button
          type="button"
          onClick={fetchMetrics}
          data-testid="metricas-retry"
          className="
            inline-flex items-center gap-2 px-4 py-2 rounded-full
            border border-[rgba(0,229,255,0.4)] text-[#00E5FF] text-[12.5px]
            hover:bg-[rgba(0,229,255,0.08)] transition-colors
          "
        >
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const todayPoint = data.last_7_days?.[data.last_7_days.length - 1] || null;
  const completadosHoy = todayPoint?.completed ?? 0;
  const totalHoy = todayPoint?.total ?? 0;

  return (
    <div className="pt-2 pb-2 flex flex-col gap-4" data-testid="metricas-page">
      {showRecordBanner ? (
        <RecordBanner
          streak={data.streak}
          onDismiss={() => setShowRecordBanner(false)}
        />
      ) : null}
      <StreakHero streak={data.streak} streakBest={data.streak_best} />
      <SevenDaysChart last_7_days={data.last_7_days} />
      <BadgesGrid
        estrella={data.estrella}
        sello={data.sello}
        completadosHoy={completadosHoy}
        totalHoy={totalHoy}
      />
    </div>
  );
}
